import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import Header from "./Header";
import Footer from "./Footer";
import { useLanguage } from "./LanguageContext";
import MentorIcon from "./logo.png";
import PrototypeFront from "./front mockup.png";

const AboutUs = () => {
  const { t } = useLanguage();
  return (
  <PageContainer>
    <Header />

    {/* ── Welcome ── */}
    <HeroSection>
      <HeroBlobs>
        <Blob style={{ top: "10%", left: "5%", width: 500, height: 500, animationDelay: "0s" }} />
        <Blob style={{ bottom: "5%", right: "10%", width: 360, height: 360, animationDelay: "-4s" }} />
      </HeroBlobs>
      <HeroOverlay />
      <HeroInner>
        <motion.div
          style={{ flex: 1 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <Eyebrow>{t("about.heroEyebrow")}</Eyebrow>
          <WelcomeTitle>
            {t("about.welcomeTitle")}<br />
            <span style={{ color: "#4CAF50" }}>{t("about.welcomeAccent")}</span>
          </WelcomeTitle>
          <WelcomeText>{t("about.welcomeText")}</WelcomeText>
        </motion.div>

        <LogoWrap
          as={motion.div}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <LogoBlob />
          <LogoImg src={MentorIcon} alt="SwingMentor logo" />
        </LogoWrap>
      </HeroInner>
    </HeroSection>

    {/* ── Elevate ── */}
    <ElevateSection>
      <motion.img
        src={PrototypeFront}
        alt="app mockup"
        whileHover={{ scale: 1.03, rotate: -1 }}
        transition={{ type: "spring", stiffness: 180, damping: 22 }}
        style={{ maxHeight: 520, maxWidth: "45%", objectFit: "contain", borderRadius: 16 }}
      />
      <ElevateContent
        as={motion.div}
        initial={{ opacity: 0, x: 40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      >
        <Eyebrow style={{ textAlign: "right" }}>{t("about.elevateEyebrow")}</Eyebrow>
        <ElevateTitle>{t("about.elevateTitle")}</ElevateTitle>
        <ElevateSub>{t("about.elevateSub")}</ElevateSub>
        <DownloadBtn>
          {t("about.downloadBtn")} <FontAwesomeIcon icon={faArrowDown} style={{ marginLeft: 8 }} />
        </DownloadBtn>
      </ElevateContent>
    </ElevateSection>

    {/* ── Mission cards ── */}
    <MissionSection>
      {["🎯","📈","🌍"].map((icon, i) => {
        const card = t("about.missionCards")[i];
        return (
          <MissionCard
            key={i}
            as={motion.div}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
          >
            <MissionIcon>{icon}</MissionIcon>
            <MissionTitle>{card.title}</MissionTitle>
            <MissionText>{card.text}</MissionText>
          </MissionCard>
        );
      })}
    </MissionSection>

    <Footer />
  </PageContainer>
  );
};

/* ── Keyframes ── */
const pulse = keyframes`
  0%, 100% { transform: scale(1);    opacity: 1; }
  50%       { transform: scale(1.12); opacity: 0.75; }
`;

const logoPulse = keyframes`
  0%, 100% { transform: scale(1) translate(-50%, -50%);    opacity: 0.55; }
  50%       { transform: scale(1.18) translate(-43%, -43%); opacity: 0.75; }
`;

/* ── Styled ── */
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100svh;
  background: #fff;
  color: #111;
  font-family: 'Montserrat', sans-serif;
`;

/* Hero */
const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  min-height: 100svh;
  background: #f5faf5;
  display: flex;
  align-items: center;
  padding: 8rem 6vw 5rem;

  @media (max-width: 768px) {
    padding: 7rem 1.5rem 4rem;
  }
`;

const HeroBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(76,175,80,0.22) 0%, transparent 70%);
  filter: blur(48px);
  animation: ${pulse} 8s ease-in-out infinite;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(100deg, rgba(245,250,245,0.9) 35%, rgba(245,250,245,0.1) 100%);
  pointer-events: none;
  z-index: 0;
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6vw;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #4CAF50;
  margin-bottom: 0.75rem;
`;

const WelcomeTitle = styled.h1`
  margin: 0 0 1.5rem;
  font-size: clamp(2.4rem, 5vw, 4rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.02em;
`;

const WelcomeText = styled.p`
  margin: 0;
  font-size: clamp(0.95rem, 1.4vw, 1.05rem);
  color: rgba(0,0,0,0.58);
  line-height: 1.75;
  max-width: 480px;
`;

/* Logo with blob */
const LogoWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const LogoBlob = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 110%;
  height: 110%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(76,175,80,0.28) 0%, transparent 70%);
  filter: blur(40px);
  animation: ${logoPulse} 6s ease-in-out infinite;
  pointer-events: none;
`;

const LogoImg = styled.img`
  position: relative;
  z-index: 1;
  width: clamp(260px, 30vw, 380px);
  border-radius: 20px;
  object-fit: contain;
`;

/* Elevate section */
const ElevateSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6vw;
  padding: 7rem 6vw;
  background: #fff;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    padding: 4rem 1.5rem;
    text-align: center;
    align-items: center;

    img { max-width: 80% !important; }
  }
`;

const ElevateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

const ElevateTitle = styled.h2`
  margin: 0.4rem 0 0.3rem;
  font-size: clamp(2rem, 4.5vw, 3.5rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.02em;
`;

const ElevateSub = styled.p`
  margin: 0 0 1.5rem;
  font-size: 1.1rem;
  color: rgba(0,0,0,0.45);
  font-style: italic;
`;

const DownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #4CAF50;
  color: #fff;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  padding: 0.9rem 2rem;
  border: 2px solid #4CAF50;
  border-radius: 4px;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #388E3C;
    border-color: #388E3C;
  }
`;

/* Mission cards */
const MissionSection = styled.section`
  padding: 5rem 6vw;
  background: #f0f7f0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 3.5rem 1.5rem;
  }
`;

const MissionCard = styled.div`
  background: #fff;
  border: 1px solid rgba(76,175,80,0.15);
  border-radius: 12px;
  padding: 2rem 1.5rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
`;

const MissionIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.75rem;
`;

const MissionTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const MissionText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: rgba(0,0,0,0.55);
  line-height: 1.65;
`;

export default AboutUs;
